import { useId, useState, type PointerEvent } from "react";
import { formatMoney, formatMoneyCompact } from "@finances/shared/money";
import styles from "./LineChart.module.css";
import { Money } from "./Money.js";
import { cx } from "./cx.js";

export interface LinePoint {
  /** An ISO date. The axis is time, so the gaps between points are real. */
  at: string;
  pence: number;
}

export interface LineSeries {
  id: string;
  label: string;
  points: LinePoint[];
  /**
   * Which colour carries it. The money tones for money figures; `on-track` for
   * a line whose whole job is to stay the right side of another one, which is
   * a verdict rather than a direction.
   */
  tone: "in" | "out" | "muted" | "on-track";
  /**
   * Dashed, unmarked, and read as a line the others are measured against
   * rather than as a reading of its own — a target, a pace, a limit.
   */
  reference?: boolean;
  /**
   * The value past which the line is in the red, drawn in the negative colour
   * from the crossing on rather than for its whole length — `alertBelow` for a
   * line that must stay up, `alertAbove` for one that must stay down.
   *
   * Colour is not carrying this on its own: the line is *past a marked line*,
   * which is the same fact stated by position, and the figures beside the
   * chart say it in words.
   */
  alertBelow?: number;
  alertAbove?: number;
}

interface LineChartProps {
  series: LineSeries[];
  /**
   * The axis, which may reach past the last point on purpose: a month still
   * running is drawn as a month with days left in it, not as one that stopped.
   */
  from: string;
  to: string;
  /** How a date reads where it is named — the axis ends, the cursor, the table. */
  formatAt: (at: string) => string;
  /**
   * Off when the caller names the lines itself — a figure row under the chart
   * carrying the same keys says it once instead of twice. Identity still has
   * to come from somewhere: `LineKey` is exported for exactly that.
   */
  legend?: boolean;
  emptyLabel?: string;
}

/**
 * A line's colour and dash, for a caller drawing its own legend. Beside the
 * words that name the line — never on its own, where it would be colour
 * carrying meaning by itself.
 */
export function LineKey({ tone, dashed = false }: { tone: LineSeries["tone"]; dashed?: boolean }) {
  return (
    <span className={cx(styles.key, styles[tone], dashed && styles.keyDashed)} aria-hidden="true" />
  );
}

const DAY = 86_400_000;

function days(date: string): number {
  return Date.parse(`${date}T00:00:00Z`) / DAY;
}

/**
 * A round number at or above this one: 1, 2, 2.5 or 5 times a power of ten.
 *
 * The top of the scale is a number somebody reads, so it is worth £1,200
 * looking like £2,000 of headroom rather than the axis topping out at
 * £1,247.30 and every gridline below it being unreadable arithmetic.
 */
function niceCeil(value: number): number {
  if (value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((candidate) => value <= candidate * magnitude) ?? 10;
  return step * magnitude;
}

/**
 * What a series is worth on a given day, interpolating between the points it
 * has. A reference line drawn from two ends still has a value on the 14th, and
 * the cursor has to be able to say what it is.
 */
function valueAt(series: LineSeries, at: string): number | null {
  const { points } = series;
  if (points.length === 0) return null;

  const exact = points.find((point) => point.at === at);
  if (exact) return exact.pence;

  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || at < first.at || at > last.at) return null;

  const after = points.findIndex((point) => point.at > at);
  const right = points[after];
  const left = points[after - 1];
  if (!right || !left) return null;

  const span = days(right.at) - days(left.at);
  const along = span === 0 ? 0 : (days(at) - days(left.at)) / span;
  return left.pence + (right.pence - left.pence) * along;
}

/**
 * The part of the plot a line is in trouble in — under the floor it has to
 * stay above, or over the ceiling it has to stay under. Null for a line with
 * no threshold, which is most of them.
 */
function alertBox(
  line: LineSeries,
  y: (pence: number) => number,
): { y: number; height: number } | null {
  if (line.alertBelow !== undefined) {
    const edge = y(line.alertBelow);
    return { y: edge, height: Math.max(100 - edge, 0) };
  }
  if (line.alertAbove !== undefined) {
    return { y: 0, height: Math.max(y(line.alertAbove), 0) };
  }
  return null;
}

/** Whether a line has ended up on the wrong side of its own threshold. */
function past(line: LineSeries, pence: number): boolean {
  if (line.alertBelow !== undefined) return pence < line.alertBelow;
  if (line.alertAbove !== undefined) return pence > line.alertAbove;
  return false;
}

/** The drawing in a sentence: every line, where it got to, over what span. */
function describe(
  series: LineSeries[],
  from: string,
  to: string,
  formatAt: (at: string) => string,
): string {
  const lines = series
    .filter((line) => line.points.length > 0)
    .map((line) => {
      const last = line.points[line.points.length - 1];
      return last ? `${line.label} ${formatMoney(last.pence)}` : null;
    })
    .filter((text): text is string => text !== null);

  return `${lines.join(", ")}, ${formatAt(from)} to ${formatAt(to)}`;
}

/**
 * Two or three figures over the same span of time, on one scale.
 *
 * A month's totals say what it came to. They cannot say whether it was steady
 * or was one weekend, or whether three weeks' spending has already gone by the
 * 9th — which is the question anyone looking at a month still in progress is
 * actually asking, and the reason this exists alongside the bar charts.
 *
 * Colour is never the only thing carrying a line: each is named in the legend
 * with its latest figure beside it, and the cursor names them again.
 *
 * What a reader who cannot see the drawing gets is a sentence — every line,
 * where it ended, over what span — rather than a hidden table of every point.
 * The figures that matter are already written out beside the chart in any
 * screen worth putting one on; a second copy of thirty-one days of running
 * totals is not a reading of the trend, it is the trend read out loud.
 */
export function LineChart({
  series,
  from,
  to,
  formatAt,
  legend = true,
  emptyLabel = "Nothing yet",
}: LineChartProps) {
  const [cursor, setCursor] = useState<string | null>(null);
  /* `useId` puts colons in, which are legal in an id and awkward in a url(). */
  const clip = useId().replaceAll(":", "");

  const drawn = series.filter((line) => line.points.length > 0);
  const readings = drawn.filter((line) => !line.reference);
  const spine = readings[0] ?? drawn[0];

  if (!spine) {
    return (
      <p className={styles.empty} role="status">
        {emptyLabel}
      </p>
    );
  }

  const values = drawn.flatMap((line) => line.points.map((point) => point.pence));
  const top = niceCeil(Math.max(...values, 1));
  const bottom = Math.min(0, -niceCeil(-Math.min(...values, 0)));
  const height = top - bottom || 1;

  const start = days(from);
  const span = Math.max(days(to) - start, 1);
  const x = (at: string) => ((days(at) - start) / span) * 100;
  const y = (pence: number) => ((top - pence) / height) * 100;

  const at = cursor ?? null;

  const move = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const along = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
    const wanted = start + along * span;
    /* The nearest point of the series being read, not the nearest day: on a
       month grouped by month there are twelve places to stop, and a cursor
       that stopped anywhere else would name a date with nothing on it. */
    const nearest = spine.points.reduce((best, point) =>
      Math.abs(days(point.at) - wanted) < Math.abs(days(best.at) - wanted) ? point : best,
    );
    setCursor(nearest.at);
  };

  return (
    <figure className={styles.chart}>
      {legend && (
        <ul className={styles.legend}>
          {series.map((line) => {
            const last = line.points[line.points.length - 1];
            return (
              <li key={line.id} className={styles.legendItem}>
                <LineKey tone={line.tone} dashed={line.reference} />
                <span className={styles.legendLabel}>{line.label}</span>
                {last && <Money pence={last.pence} tone="none" size="sm" weight="semibold" />}
              </li>
            );
          })}
        </ul>
      )}

      <div className={styles.plot} role="img" aria-label={describe(series, from, to, formatAt)}>
        <span className={styles.gridline} style={{ top: "0%" }} aria-hidden="true" />
        <span className={styles.gridline} style={{ top: "50%" }} aria-hidden="true" />
        <span className={styles.baseline} style={{ top: `${String(y(0))}%` }} aria-hidden="true" />
        <span className={styles.scale} style={{ top: "0%" }} aria-hidden="true">
          {formatMoneyCompact(top)}
        </span>
        <span className={styles.scale} style={{ top: "50%" }} aria-hidden="true">
          {formatMoneyCompact((top + bottom) / 2)}
        </span>

        <svg
          className={styles.lines}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/**
           * The red is the same line drawn twice, the second time clipped to
           * the wrong side of the threshold — so the colour changes at the
           * crossing itself rather than at the point either side of it, and no
           * arithmetic has to find where two points straddle it.
           */}
          <defs>
            {drawn.map((line) => {
              const box = alertBox(line, y);
              return box === null ? null : (
                <clipPath key={line.id} id={`${clip}-${line.id}`} clipPathUnits="userSpaceOnUse">
                  <rect x="0" width="100" y={box.y} height={box.height} />
                </clipPath>
              );
            })}
          </defs>
          {drawn.map((line) => {
            const shape = line.points
              .map((point) => `${x(point.at).toFixed(2)},${y(point.pence).toFixed(2)}`)
              .join(" ");

            return (
              <g key={line.id}>
                <polyline
                  className={cx(styles.line, styles[line.tone], line.reference && styles.dashed)}
                  vectorEffect="non-scaling-stroke"
                  points={shape}
                />
                {alertBox(line, y) !== null && (
                  <polyline
                    className={cx(styles.line, styles.negative)}
                    clipPath={`url(#${clip}-${line.id})`}
                    vectorEffect="non-scaling-stroke"
                    points={shape}
                  />
                )}
              </g>
            );
          })}
        </svg>

        {readings.map((line) => {
          const last = line.points[line.points.length - 1];
          const red = last && past(line, last.pence);
          return last ? (
            <span
              key={line.id}
              className={cx(styles.head, red ? styles.negative : styles[line.tone])}
              style={{ left: `${String(x(last.at))}%`, top: `${String(y(last.pence))}%` }}
              aria-hidden="true"
            />
          ) : null;
        })}

        {at && (
          <span
            className={styles.cursor}
            style={{ left: `${String(x(at))}%` }}
            aria-hidden="true"
          />
        )}

        <div
          className={styles.surface}
          onPointerMove={move}
          onPointerLeave={() => setCursor(null)}
        />

        {at && (
          <div
            className={styles.tooltip}
            style={{ left: `${String(Math.min(Math.max(x(at), 12), 88))}%` }}
            role="status"
          >
            <span className={styles.tooltipDate}>{formatAt(at)}</span>
            {series.map((line) => {
              const value = valueAt(line, at);
              return value === null ? null : (
                <span key={line.id} className={styles.tooltipRow}>
                  <LineKey tone={line.tone} dashed={line.reference} />
                  {line.label}
                  <Money pence={Math.round(value)} tone="none" size="sm" weight="semibold" />
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.axis}>
        <span>{formatAt(from)}</span>
        <span>{formatAt(to)}</span>
      </div>
    </figure>
  );
}
